// Update the props interface in StatementUploadPage.tsx
import React, { useState, useEffect, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Upload,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  RefreshCw,
  OctagonX,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { get } from 'lodash';

import { useConfig } from 'config/ConfigContext';
import { deleteStatementAction, getStatementsAction, processStatementAction, restoreStatementAction } from 'actions/bankStatements';
import { nextAction } from 'actions/workflow';
import { Grid, Column } from 'components/Layout';
import { Card, CardContent } from 'components/BankStatements/ui/card';
import { formatDate } from 'utils/bankStatements';
import { getBlackbox, initIovation } from 'utils/iovation';
import { useToast } from 'components/BankStatements/ui/toast';
import UploadGuidelines from 'components/BankStatements/UploadGuidelines';
import { Button } from 'components/Button';
import NeedHelpModal from 'components/BankStatements/NeedHelpModal';

let bankWebSocket = null;
const NORMAL_CLOSE = 'Normal close';

const MAXIMUM_FILE_SIZE_MB = 10;

export const StatementUploadPage = ({ onStatementSelect, bankProcessorKey, applicationId }) => {
  const appConfig = useConfig();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useToast();
  const statements = useSelector((state) => get(state, 'bankStatements.statements') || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const key = searchParams.get('key');
  const hasNeedsReviewApps = useMemo(() => (statements || []).filter((s) => !s.isDeleted && s.hasValidationIssues).length > 0 , [statements]) 

  const loadStatements = () => {
    if (!applicationId) return;

    setIsLoading(true);

    dispatch(getStatementsAction({
      entityId: applicationId,
      entityType: 'application',
      bankProcessorKey,
      success: () => {
        setIsLoading(false);
      },
      fail: () => {
        setIsLoading(false);
        showToast('Failed to load statements', 'error');
      },
    }));
  };

  const listenBankStatementWebSocket = () => {
    bankWebSocket = new WebSocket(`${appConfig.websocketUrl}?application_id=${applicationId}`);
    console.log('connection:', bankWebSocket);
    bankWebSocket.addEventListener('message', (event) => {
      console.log('eventData:', event.data);
      const eventData = JSON.parse(get(event, 'data'));
      const eventType = get(eventData, 'EventType');
      if (eventType === 'BANK_STATEMENT_PROCESSING_COMPLETE') {
        loadStatements();
      }
    });
    bankWebSocket.addEventListener('close', (event) => {
      if (get(event, 'reason') !== NORMAL_CLOSE || get(event, 'code') !== 1000) {
        console.log('Closed and Reconnecting:', event);
        listenBankStatementWebSocket();
      }
    });
  };

  useEffect(() => {
    initIovation();
  }, []);

  useEffect(() => {
    if (applicationId) {
      loadStatements();
      listenBankStatementWebSocket();
    }

    return () => {
      bankWebSocket && bankWebSocket.close(1000, NORMAL_CLOSE);
      bankWebSocket = null;
    };
  }, [applicationId]);

  const handleSubmit = () => {
    setIsSubmitting(true);
    const iovationBlackBox = getBlackbox();

    dispatch(nextAction({
      data: {
        iovationBlackBox: iovationBlackBox || '',
      },
      url: `/workflows/fsl/${key}/next`,
      success: (response) => {
        const routeUrl = response.data && response.data.url;
        navigate(routeUrl);
      },
      fail: (error) => {
        console.log(error);
        setIsSubmitting(false);
        showToast(error.message || 'An error occurred', 'error');
      },
    }));
  }

  const processStatement = async (file) => {
    if (!applicationId) {
      showToast('Application ID is required', 'error');
      return;
    }

    setIsProcessing(true);
    console.log(`Processing statement: ${file.name} for application ID: ${applicationId}`);

    const formData = new FormData();
    formData.append('file', file);

    dispatch(processStatementAction({
      entityType: 'application',
      entityId: applicationId,
      data: formData,
      bankProcessorKey,
      success: async (res) => {
        if (!res.success) {
          showToast(res.error.message, 'error');
          setIsProcessing(false);
          return;
        }
        showToast('Statement processed successfully', 'success');
        loadStatements();
        setIsProcessing(false);
      },
      fail: (error) => {
        setIsProcessing(false);
        console.error('Processing error:', error);
        if (error instanceof Error) {
          showToast(error.message || 'Failed to process statement', 'error');
        } else {
          showToast(
            'An unexpected error occurred while processing the statement',
            'error'
          );
        }
      },
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const { 0: file } = e.dataTransfer.files;
    if (file && file.type === 'application/pdf') {
      if (file.size > MAXIMUM_FILE_SIZE_MB * 1024 * 1024) {
        showToast('File cannot be processed. Maximum file size is 10MB.', 'error');
        return;
      }
      await processStatement(file);
    } else {
      showToast('Please upload a PDF file.', 'error');
    }
  };

  const handleFileInput = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      console.log('File selected:', file.name, file.size, file.type);
      if (file.size > MAXIMUM_FILE_SIZE_MB * 1024 * 1024) {
        showToast('File cannot be processed. Maximum file size is 10MB.', 'error');
        return;
      }
      await processStatement(file);
    }
  };

  const renderStatusBadge = (statement) => {
    if (statement.isDeleted) {
      return <span className="confidence-badge deleted cursor-not-allowed">Deleted</span>;
    }

    if (statement.processingStatus === 'Failed') {
      return (
        <span
          className="confidence-badge review text-sm cursor-not-allowed"
          title="Processing failed."
        >
          <OctagonX className="w-4 h-4 mr-1" />
          Failed
        </span>
      );
    }
    if (statement.processingStatus === 'InProgress') {
      return (
        <span
          className="confidence-badge cursor-not-allowed processing text-sm"
          title="Processing in progress."
          onClick={() => onStatementSelect(statement.bankStatementId)}
        >
          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
          Processing...
        </span>
      );
    }

    // Use the hasValidationIssues flag directly from the statement
    const hasIssues = statement.hasValidationIssues === true;

    return hasIssues ? (
      <span
        className="confidence-badge review"
        title="Action needed! Click to Review and resolve issues."
        onClick={() => onStatementSelect(statement.bankStatementId)}
      >
        <AlertTriangle className="w-4 h-4 mr-1" />
        Needs Review
      </span>
    ) : (
      <span
        onClick={() => onStatementSelect(statement.bankStatementId)}
        className="confidence-badge verified"
        title="Looks Great! Bank Statement Uploaded Successfully."
      >
        <CheckCircle2 className="w-4 h-4 mr-1" />
        OK
      </span>
    );
  };

  const deleteStatement = async (e, statementId) => {
    e.stopPropagation();
    setIsLoading(true);

    dispatch(deleteStatementAction({
      statementId,
      entityType: 'application',
      entityId: applicationId,
      bankProcessorKey,
      success: () => {
        showToast('Statement deleted successfully', 'success');
        setIsLoading(false);
        loadStatements();
      },
      fail: (error) => {
        console.log(error);
        setIsLoading(false);
        showToast(error.message || 'Failed to delete statement', 'error');
      },
    }));
  };

  const restoreStatement = async (e, statementId) => {
    e.stopPropagation();
    setIsLoading(true);

    dispatch(restoreStatementAction({
      statementId: statementId,
      entityType: 'application',
      entityId: applicationId,
      bankProcessorKey,
      success: () => {
        showToast('Statement restored successfully', 'success');
        setIsLoading(false);
        loadStatements();
      },
      fail: (error) => {
        console.log(error);
        setIsLoading(false);
        showToast(error.message || 'Failed to restore statement', 'error');
      },
    }));
  }

  return (
    <>
      <section className="container section">
        <div className="grid-container fluid bank-statement-page">
          <div className="grid-container">
            <Grid className="grid-margin-x max-limited">
              {applicationId ? (
                <div className="cell small-12 medium-12" style={{ textAlign: 'left' }}>
                  <p className="mb-0 p-large"><b>Application ID:</b> {applicationId}</p>
                </div>
              ) : null}

              <Column className="small-12 relative flex flex-col-reverse">
                <h2 className="text-lg md:text-xl text-center font-medium text-gray-800 mt-0 md:mt-8 mb-6">Upload Bank Statement</h2>
                <div className="static -top-9 right-0 z-10 flex justify-center md:absolute mt-4 md:mt-0 md:mb-0 mb-4">
                  <UploadGuidelines />
                </div>
              </Column>

              <Column className="small-12">
                <Card className="upload-section">
                  <CardContent className="p-0">
                    <div
                      className={`w-full relative border border-gray-200 rounded-lg p-12 flex flex-col items-center justify-center ${
                        isDragging ? "border-green-500 bg-green-50" : ""
                      } hover:border-green-500 hover:bg-green-50 transition-colors duration-200`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      {isProcessing ? (
                        <div style={{ textAlign: 'center' }}>
                          <div
                            className="loading-spinner"
                            style={{ display: 'inline-block' }}
                          />
                          <p className="text-gray-600 text-lg font-medium mt-3">
                            Processing statement...
                          </p>
                        </div>
                      ) : (
                        <Fragment>
                          <p className="text-gray-600 mb-6 text-center text-sm md:text-base">
                            Drag and drop your PDF statement here
                          </p>
                          <div className="flex items-center justify-center">
                            <Button className="button text-sm md:text-base green flex items-center px-4 py-2 text-white rounded-lg font-normal mb-0 !border-0">
                              <Upload className="mr-2 h-4 w-4" />
                              Choose File
                            </Button>
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={handleFileInput}
                              disabled={isProcessing}
                              style={{
                                position: 'absolute',
                                top: 0,
                                margin: 0,
                                left: 0,
                                opacity: 0,
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer',
                              }}
                            />
                          </div>
                        </Fragment>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Column>

              <Column className="small-12">
                <Card className="upload-section">
                  <CardContent className="p-0">
                    <div className="flex flex-col hidden md:!block">
                      {/* Main title and info row */}
                      <h2 className="text-xl font-medium text-gray-800 mb-2">All Statements</h2>
          
                      {hasNeedsReviewApps && (
                        <p className="text-sm text-gray-600 mb-4 font-medium">
                          Click the NEEDS REVIEW button to verify your transactions
                        </p>
                      )}
                    </div>
                    <div className="table-wrapper">
                      <table className="statement-table">
                        <thead>
                          <tr>
                            <th>Bank Name</th>
                            <th className="hidden md:!table-cell">Account Number</th>
                            <th>Statement Period</th>
                            <th className="hidden md:!table-cell">Status</th>
                            <th className="hidden md:!table-cell">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan={5} className="text-center py-8">
                                <div className="mx-auto mt-2 w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                                <p className="mt-2 text-gray-600">
                                  Loading statements...
                                </p>
                              </td>
                            </tr>
                          ) : statements.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="text-center py-8">
                                <p className="text-gray-600">No statements found</p>
                              </td>
                            </tr>
                          ) : (
                            statements
                              // Add deduplication filter to ensure unique statement IDs
                              .filter((statement, index, self) => index === self.findIndex(s => s.bankStatementId === statement.bankStatementId))
                              // Sort by statement date (newest first)
                              .sort((a, b) => {
                                // Use statementEndDate for sorting (newest first)
                                const dateA = new Date(a.statementEndDate || '');
                                const dateB = new Date(b.statementEndDate || '');
                                return dateA.getTime() - dateB.getTime();
                              })
                              .map(statement => (
                                <tr
                                  key={statement.bankStatementId}
                                  className={statement.isDeleted ? 'opacity-50' : ''}
                                >
                                  <td>
                                    {statement.processingStatus === 'Done' ? (statement.bankName || (<span className="text-red-500 italic">Bank Name not captured</span>)) : (statement.originalFileName || 'N/A')}
                                  </td>
                                  <td className="hidden md:!table-cell">
                                    {(statement.accountNumbers && statement.accountNumbers.join(', ')) || 'N/A'}
                                  </td>
                                  <td>
                                    {!(statement.statementStartDate && statement.statementEndDate) ? 'N/A' : `${formatDate(statement.statementStartDate)} to ${formatDate(statement.statementEndDate)}`}
                                  </td>
                                  <td className="hidden md:!table-cell">
                                    {renderStatusBadge(statement)}
                                  </td>
                                  <td className="hidden md:!table-cell">
                                    {statement.processingStatus === 'Done' && !statement.isDeleted ? (
                                      <button
                                        type="button"
                                        onClick={(e) => deleteStatement(e, statement.bankStatementId)}
                                        className="p-2 hover:bg-gray-100 rounded-full"
                                        title="Click to delete. Don't worry, you can undo it!"
                                      >
                                        <Trash2 className="h-4 w-4 text-gray-500" />
                                      </button>
                                    ) : statement.processingStatus === 'Done' && (
                                      <button
                                        type="button"
                                        onClick={(e) => restoreStatement(e, statement.bankStatementId)}
                                        className="p-2 hover:bg-gray-100 rounded-full"
                                        title="Restore Statement"
                                      >
                                        <RefreshCw aria-label="Restore Statement" className="h-4 w-4 text-gray-500" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </Column>

              <Column className="small-12 text-center mt-3">
                <div className="flex justify-center mb-6">
                  <Button
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    isDisabled={!statements || statements.filter((s) => !s.isDeleted).length === 0}
                    className="button text-sm md:text-base green w-full max-w-md py-3 text-white rounded-lg font-medium mb-0 !border-0"
                  >
                    Continue to Final Review
                  </Button>
                </div>
                <NeedHelpModal />
              </Column>
            </Grid>
          </div>
        </div>
      </section>
    </>
  );
};

StatementUploadPage.propTypes = {
  onStatementSelect: PropTypes.func.isRequired,
  applicationId: PropTypes.string.isRequired,
  bankProcessorKey: PropTypes.string.isRequired,
};

export default StatementUploadPage;
